import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, CalendarClock, ArrowRight, Calendar } from 'lucide-react';
import { RESERVATIONS, APPOINTMENTS } from '../../../utilities/constants/AccountScreen';
import KeycloakService from '../../config/KeycloakService';
import { getAppointmentsByUserId } from '../../appointment/AppointmentServices';
import { getReservationByUserId } from '../../reservation/ReservationServices';
import { useLoadingSpinner } from '../../../utilities/components/LoadingSpinnerProvider';
import NextEventHero from './NextEventHero';

const CANCELLED_STATUSES = new Set(['CANCELLED', 'Cancelled', 'Canceled', 'CANCELLED_BY_USER']);
const hasActiveStatus = (item) => !CANCELLED_STATUSES.has(item?.status);

const isUpcomingActiveItem = (item, dateField) => {
   const status = item?.status;
   const dateValue = item?.[dateField];

   if (!dateValue || CANCELLED_STATUSES.has(status)) {
     return false;
   }

   return new Date(dateValue) > new Date();
 };

const Overview = () => {
  const navigate = useNavigate();
  const setIsLoading = useLoadingSpinner();
  const [stats, setStats] = useState({
    reservations: 0,
    appointments: 0,
    upcomingCount: 0,
    nextEvent: null
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const userId = KeycloakService.getUserId();

        // Fetch raw data (unpaged/large page size to get all upcoming)
        // Note: In a real app, you'd likely have a specific "dashboard stats" endpoint
        // For now, we fetch a reasonable amount to find the "next" event
        const queryParams = { page: 0, pageSize: 100 };

        // Parallel fetch
        // Parallel fetch
        let apptData = null;
        let resData = null;

        await Promise.all([
          getAppointmentsByUserId((data) => { apptData = data; }, userId, () => { }, () => { }, queryParams),
          getReservationByUserId(userId, queryParams, (data) => { resData = data; }, () => { }, () => { })
        ]);

        // Process Appointments
        const allActiveAppointments = (apptData?.content || []).filter(hasActiveStatus);
        const allActiveReservations = (resData?.content || []).filter(hasActiveStatus);

        const activeAppointments = (apptData?.content || []).filter((appointment) =>
           isUpcomingActiveItem(appointment, 'dateTime'),
         );

         const activeReservations = (resData?.content || []).filter((reservation) =>
           isUpcomingActiveItem(reservation, 'startingDateTime'),
         );

         // Process Appointments
         const appointmentEvents = allActiveAppointments.map(a => ({
            id: a.id,
            type: 'appointment',
            title: a.raison || 'Appointment',
            dateTime: a.dateTime,
            link: `/account/${APPOINTMENTS}`
          }));

         // Process Reservations
         const reservationEvents = allActiveReservations.map(r => ({
            id: r.id,
            type: 'reservation',
            title: r.eventType,
            dateTime: r.startingDateTime,
            link: `/account/${RESERVATIONS}`
          }));

         // Combine and Sort
        const allEvents = [...appointmentEvents, ...reservationEvents];

        const futureEvents = [...activeAppointments.map(a => ({
          id: a.id,
          type: 'appointment',
          title: a.raison || 'Appointment',
          dateTime: a.dateTime,
          link: `/account/${APPOINTMENTS}`
        })), ...activeReservations.map(r => ({
          id: r.id,
          type: 'reservation',
          title: r.eventType,
          dateTime: r.startingDateTime,
          link: `/account/${RESERVATIONS}`
        }))];

        futureEvents.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

        const recentEvents = [...allEvents]
          .filter((event) => event.dateTime)
          .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
          .slice(0, 5);

          setStats({
          reservations: allActiveReservations.length,
          appointments: allActiveAppointments.length,
            upcomingCount: futureEvents.length,
            nextEvent: futureEvents.length > 0 ? futureEvents[0] : null,
           recentEvents
         });

       } catch (error) {
         console.error("Failed to load dashboard data", error);
       } finally {
         setIsLoading(false);
       }
    };

    fetchData();
  }, [setIsLoading]);

  const statCards = [
    {
      title: 'Total Bookings',
      value: stats.reservations,
      icon: <CalendarDays className="text-white" size={24} />,
      bg: 'bg-blue-500',
      link: `/account/${RESERVATIONS}`
    },
    {
      title: 'Appointments',
      value: stats.appointments,
      icon: <CalendarClock className="text-white" size={24} />,
      bg: 'bg-purple-500',
      link: `/account/${APPOINTMENTS}`
    },
    {
      title: 'Upcoming Events',
      value: stats.upcomingCount,
      icon: <Calendar className="text-white" size={24} />,
      bg: 'bg-orange-500',
      description: 'Reservations & Appointments',
      links: [
        { label: 'Reservations', path: `/account/${RESERVATIONS}` },
        { label: 'Appointments', path: `/account/${APPOINTMENTS}` }
      ]
    },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Dashboard Overview</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Here's what's happening with your account.</p>
      </div>

      {/* Hero Section */}
      <NextEventHero nextEvent={stats.nextEvent} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            onClick={() => stat.link && navigate(stat.link)}
            className={`bg-white dark:bg-slate-900 rounded-xl p-6 shadow-sm border border-slate-200 dark:border- Slate-800 hover:shadow-md transition-shadow group relative ${stat.link ? 'cursor-pointer' : ''}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.bg} p-3 rounded-lg shadow-lg shadow-blue-500/20`}>
                {stat.icon}
              </div>
              {stat.link && (
                <span className="text-slate-400 group-hover:text-blue-500 transition-colors">
                  <ArrowRight size={20} />
                </span>
              )}
            </div>
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{stat.value}</h3>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{stat.title}</p>
            {stat.description && (
              <p className="text-xs text-slate-400 mt-1">{stat.description}</p>
            )}

            {/* Multi-link Buttons */}
            {stat.links && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                {stat.links.map(link => (
                  <button
                    key={link.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(link.path);
                    }}
                    className="flex-1 text-xs py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium transition-colors text-center"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recent Activity Section */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Recent Activity</h3>
          <button
            onClick={() => navigate(`/account/${RESERVATIONS}`)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            View All
          </button>
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {stats.recentEvents && stats.recentEvents.length > 0 ? (
            stats.recentEvents.map((event) => (
              <div
                key={`${event.type}-${event.id}`}
                onClick={() => navigate(event.link)}
                className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors flex items-center justify-between group cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${event.type === 'reservation' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30'}`}>
                    {event.type === 'reservation' ? <CalendarDays size={20} /> : <CalendarClock size={20} />}
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {event.title || 'Untitled Event'}
                    </h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {new Date(event.dateTime).toLocaleDateString()} at {new Date(event.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-slate-400 group-hover:text-blue-500 transition-colors text-sm font-medium">
                  Details <ArrowRight size={16} />
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center">
              <div className="inline-flex justify-center items-center w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 mb-3">
                <Calendar size={24} />
              </div>
              <p className="text-slate-500 dark:text-slate-400">No recent activity to show.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Overview;
